import React, { useMemo } from "react";
import Box from "@mui/material/Box/Box";
import GenieText from "../../../../components/core/genie-text/GenieText";
import { Alert, AlertTitle, Button } from "@mui/material";
import LineItem from "../line-item/LineItem";
import { ArrowBack } from "@mui/icons-material";
import { Link as RouterLink, useParams } from "react-router-dom";
import useCurrentCart from "../../../../queries/routes/useCurrentCart";
import useUser from "../../../../queries/routes/useUser";
import { lineItemQuantity } from "../../../../queries/utils/calculate";
import { formatCurrency } from "../../../../utils/currency";
import GenieSkeleton from "../../../../components/core/genie-skeleton/GenieSkeleton";
import GenieEmpty from "../../../../components/core/genie-empty/GenieEmpty";
import useBuyerOrders from "../../../../queries/routes/useBuyerOrders";

const CART_WIDTH = 300;

export const MINIMUM_LINE_ITEMS = 10;

interface CartProps {
  closeDrawer: () => void;
}

function Cart(props: CartProps) {
  const { org_id } = useParams();
  const { data: user } = useUser();
  const { data: cart, isLoading } = useCurrentCart(
    org_id!,
    user?.organizations[0].id!
  );

  const orderTerms = user?.purchasing_organizations.find(
    (org) => org.organization.id === org_id
  )?.organization.order_terms;

  const { data: orders } = useBuyerOrders(user?.organizations[0].id!);

  const totalLineItems = useMemo(() => {
    return lineItemQuantity(cart);
  }, [cart]);

  const minimum =
    orders && orders?.length > 0
      ? orderTerms?.returning_customer_min || 0
      : orderTerms?.new_customer_min || 0;

  const minimumCents =
    orders && orders?.length > 0
      ? orderTerms?.returning_customer_min_cents || 0
      : orderTerms?.new_customer_min_cents || 0;

  const exceedsMinimum = totalLineItems >= minimum;
  const exceedsMinimumCents = cart && cart?.total_cents >= minimumCents;

  const { closeDrawer } = props;

  return (
    <Box width={CART_WIDTH}>
      <Box
        borderBottom={1}
        position={"fixed"}
        width={CART_WIDTH}
        top={0}
        sx={{ backgroundColor: "white", zIndex: 1, borderColor: "grey.300" }}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          p={2}
        >
          <Box display={"flex"} alignItems={"center"}>
            <ArrowBack onClick={closeDrawer} cursor={"pointer"} />
            <GenieText style={{ ml: 1 }} fontWeight={"bold"} fontSize={"small"}>
              Cart
            </GenieText>
          </Box>
          <GenieText fontWeight={"bold"} fontSize={"tiny"}>
            {totalLineItems} Items
          </GenieText>
        </Box>
      </Box>
      <Box mt={10} px={2} mb={16}>
        {orders &&
          (!exceedsMinimum || !exceedsMinimumCents) &&
          cart &&
          cart?.line_items.length > 0 && (
            <Alert sx={{ width: "100%", mb: 2 }} severity="warning">
              <AlertTitle>Cannot Checkout</AlertTitle>
              {!exceedsMinimum && (
                <Box mb={1}>
                  Your wholesale order must include at least {minimum} items to
                  checkout.
                </Box>
              )}
              {!exceedsMinimumCents && (
                <Box>
                  Your wholesale order must cost at least{" "}
                  {formatCurrency(minimumCents)} items to checkout.
                </Box>
              )}
            </Alert>
          )}
        {!isLoading && cart?.line_items.length === 0 && (
          <GenieEmpty>Cart is Empty</GenieEmpty>
        )}
        {cart?.line_items.map((lineItem) => {
          return <LineItem key={lineItem.id} lineItem={lineItem} cart={cart} />;
        })}
      </Box>
      <Box
        position={"fixed"}
        width={CART_WIDTH}
        bottom={0}
        sx={{ backgroundColor: "white", zIndex: 1, borderColor: "grey.300" }}
        borderTop={1}
        px={2}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          py={2}
        >
          <GenieText fontWeight={"bold"} fontSize={"small"}>
            Estimated Total
          </GenieText>
          <GenieText fontWeight={"bold"} fontSize={"tiny"}>
            {!cart ? (
              <GenieSkeleton width={"100%"} height={24} />
            ) : (
              formatCurrency(cart.total_cents)
            )}
          </GenieText>
        </Box>
        <Box mb={2}>
          <Button
            component={RouterLink}
            disabled={
              cart?.line_items.length === 0 ||
              !exceedsMinimum ||
              !exceedsMinimumCents
            }
            to={`/storefront/${org_id}/checkout`}
            onClick={() => {
              closeDrawer();
            }}
            variant={"outlined"}
            fullWidth
          >
            Checkout
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Cart;
